/** Path / nav permission helpers for RBAC. */

import {
  SECTION_CATALOG,
  findSectionByPath,
  isSectionPermissionCode,
  sectionActionCode,
} from './sectionCatalog';

export const ADMIN_ROLE_CODE = 'administrador';

export {
  SECTION_ACTIONS,
  SECTION_CATALOG,
  SECTION_TREE,
  derivedModuleCodesFromSections,
  findSectionByPath,
  isSectionPermissionCode,
  sectionActionCode,
} from './sectionCatalog';

/** Longest-prefix wins. */
const PATH_PERMISSION_RULES = [
  { prefix: '/sistema/admin/users', permission: 'admin.users' },
  { prefix: '/sistema/admin/permissions', permission: 'admin.permissions' },
  { prefix: '/sistema/datos/importacion', permission: 'system.data.import' },
  { prefix: '/sistema/datos/exportacion', permission: 'system.data.export' },
  { prefix: '/sistema/company', permission: 'admin.access' },
  { prefix: '/sistema/monitoring', permission: 'admin.access' },
  { prefix: '/administracion', permission: 'admin.access' },
  { prefix: '/sistema', permission: 'admin.access' },
  { prefix: '/backoffice/finance', permission: 'finance.read' },
  { prefix: '/backoffice/dashboard', permission: 'backoffice.dashboard' },
  { prefix: '/backoffice/analytics', permission: 'backoffice.dashboard' },
  { prefix: '/backoffice/monitoring', permission: 'backoffice.dashboard' },
  { prefix: '/backoffice/settings', permission: 'backoffice.dashboard' },
  { prefix: '/backoffice/hr', permission: 'backoffice.dashboard' },
  { prefix: '/backoffice/acquisitions', permission: 'procurement.read' },
  { prefix: '/backoffice', permission: 'backoffice.dashboard' },
  { prefix: '/pinturas/products', permission: 'products.read' },
  { prefix: '/pinturas/commercial', permission: 'commercial.read' },
  { prefix: '/pinturas/production', permission: 'production.read' },
  { prefix: '/pinturas/quality', permission: 'production.read' },
  { prefix: '/pinturas/inventory', permission: 'production.read' },
  { prefix: '/pinturas/acquisitions', permission: 'procurement.read' },
  { prefix: '/pinturas/suppliers', permission: 'procurement.read' },
  { prefix: '/pinturas', permission: 'commercial.read' },
  { prefix: '/aditivos/products', permission: 'additives.commercial.read' },
  { prefix: '/aditivos/commercial', permission: 'additives.commercial.read' },
];

const HREF_PERMISSION = {
  '/backoffice/dashboard': 'backoffice.dashboard',
  '/backoffice/analytics': 'backoffice.dashboard',
  '/backoffice/monitoring': 'backoffice.dashboard',
  '/backoffice/settings': 'backoffice.dashboard',
  '/backoffice/hr/collaborators': 'backoffice.dashboard',
  '/backoffice/hr/team': 'backoffice.dashboard',
  '/backoffice/hr/recruitment': 'backoffice.dashboard',
  '/backoffice/hr/performance': 'backoffice.dashboard',
  '/backoffice/hr/training': 'backoffice.dashboard',
  '/backoffice/hr/attendance': 'backoffice.dashboard',
  '/backoffice/finance/overview': 'finance.read',
  '/backoffice/finance/treasury': 'finance.read',
  '/backoffice/finance/accounting': 'finance.read',
  '/backoffice/finance/stakeholders': 'finance.read',
  '/backoffice/finance/taxes': 'finance.read',
  '/backoffice/finance/purchases': 'finance.read',
  '/backoffice/finance/budget': 'finance.read',
  '/backoffice/finance/payroll': 'finance.read',
  '/backoffice/finance/statements': 'finance.read',
  '/backoffice/finance/financing': 'finance.read',
  '/backoffice/finance/settings': 'finance.read',
  '/pinturas/products/paint': 'products.read',
  '/pinturas/products/upselling': 'products.read',
  '/pinturas/products/competitors': 'products.read',
  '/pinturas/commercial/summary': 'commercial.read',
  '/pinturas/commercial/config': 'commercial.read',
  '/pinturas/commercial/analysis': 'commercial.read',
  '/pinturas/commercial/leads': 'commercial.read',
  '/pinturas/commercial/prospecting': 'commercial.read',
  '/pinturas/commercial/tenders': 'commercial.read',
  '/pinturas/commercial/quotes': 'commercial.read',
  '/pinturas/commercial/sales': 'commercial.read',
  '/pinturas/commercial/requests': 'commercial.read',
  '/pinturas/commercial/logistics': 'commercial.read',
  '/pinturas/commercial/after-sales': 'commercial.read',
  '/pinturas/production/config': 'production.read',
  '/pinturas/production/raw-materials': 'production.read',
  '/pinturas/production/formulas': 'production.read',
  '/pinturas/production/planning': 'production.read',
  '/pinturas/production/shop': 'production.read',
  '/pinturas/production/batches': 'production.read',
  '/pinturas/quality': 'production.read',
  '/pinturas/inventory': 'production.read',
  '/pinturas/suppliers': 'procurement.read',
  '/pinturas/acquisitions/summary': 'procurement.read',
  '/pinturas/acquisitions/config': 'procurement.read',
  '/pinturas/acquisitions/purchase-requests': 'procurement.read',
  '/pinturas/acquisitions/purchase-orders': 'procurement.read',
  '/pinturas/acquisitions/receipts': 'procurement.read',
  '/pinturas/acquisitions/quotations': 'procurement.read',
  '/pinturas/acquisitions/inventory': 'procurement.read',
  '/pinturas/acquisitions/catalog': 'procurement.read',
  '/pinturas/acquisitions/raw-materials': 'procurement.read',
  '/aditivos/products/standards': 'additives.commercial.read',
  '/aditivos/products/phenox': 'additives.commercial.read',
  '/aditivos/products/competitors': 'additives.commercial.read',
  '/aditivos/products/patents': 'additives.commercial.read',
  '/aditivos/products/formulas': 'additives.commercial.read',
  '/aditivos/commercial/summary': 'additives.commercial.read',
  '/aditivos/commercial/config': 'additives.commercial.read',
  '/aditivos/commercial/leads': 'additives.commercial.read',
  '/aditivos/commercial/prospecting': 'additives.commercial.read',
  '/aditivos/commercial/market': 'additives.commercial.read',
  '/administracion': 'admin.access',
  '/sistema/admin/users': 'admin.users',
  '/sistema/admin/permissions': 'admin.permissions',
  '/sistema/datos/importacion': 'system.data.import',
  '/sistema/datos/exportacion': 'system.data.export',
};

const CATALOG_ITEM_PERMISSION = {
  users: 'admin.users',
  permissions: 'admin.permissions',
  maintenance: 'admin.access',
  general: 'admin.access',
  configuration: 'admin.access',
  activity: 'admin.access',
  sessions: 'monitoring.presence',
  importacion: 'system.data.import',
  exportacion: 'system.data.export',
};

export function getPermissionCodes(user) {
  return Array.isArray(user?.permissions) ? user.permissions : [];
}

export function getRoleCodes(user) {
  if (Array.isArray(user?.role_codes)) return user.role_codes;
  if (Array.isArray(user?.roles)) return user.roles.map((r) => r.code).filter(Boolean);
  return [];
}

export function hasPermission(user, code) {
  if (!code) return true;
  return getPermissionCodes(user).includes(code);
}

export function hasAnySectionPermissions(user) {
  return getPermissionCodes(user).some((code) => isSectionPermissionCode(code));
}

export function hasSectionAction(user, pathOrSection, action = 'read') {
  const section =
    pathOrSection && typeof pathOrSection === 'object' && pathOrSection.unitKey
      ? pathOrSection
      : findSectionByPath(pathOrSection);
  if (!section) return true;
  if (hasPermission(user, sectionActionCode(section, action))) return true;
  if (!hasAnySectionPermissions(user)) {
    if (action === 'read') {
      if (section.legacyRead?.length) return hasAnyPermission(user, section.legacyRead);
      return Boolean(section.ungatedFallback);
    }
    if (section.legacyWrite?.length) return hasAnyPermission(user, section.legacyWrite);
    return false;
  }
  return false;
}

export function unitHasVisibleSection(user, unitName) {
  const unit = String(unitName || '');
  const sections = SECTION_CATALOG.filter((section) => section.unit === unit);
  if (!sections.length) return true;
  if (sections.some((section) => hasSectionAction(user, section, 'read'))) return true;
  if (hasAnySectionPermissions(user)) return false;
  if (unit === 'Pinturas') {
    return hasAnyPermission(user, ['commercial.read', 'products.read', 'production.read', 'procurement.read']);
  }
  if (unit === 'BackOffice') {
    return hasAnyPermission(user, ['backoffice.dashboard', 'finance.read']);
  }
  if (unit === 'Aditivos') {
    return isSystemAdmin(user) || hasPermission(user, 'additives.commercial.read');
  }
  return true;
}

export function hasAnyPermission(user, codes = []) {
  if (!codes.length) return true;
  const set = new Set(getPermissionCodes(user));
  return codes.some((c) => set.has(c));
}

export function hasRole(user, roleCode) {
  return getRoleCodes(user).includes(roleCode);
}

export function isSystemAdmin(user) {
  return hasRole(user, ADMIN_ROLE_CODE);
}

export function canAccessAdministration(user) {
  return isSystemAdmin(user);
}

function isAdministrationAreaPath(pathname = '') {
  const path = String(pathname || '');
  return (
    path === '/administracion' ||
    path.startsWith('/administracion/') ||
    path === '/sistema' ||
    path.startsWith('/sistema/')
  );
}

export function requiredPermissionForPath(pathname = '') {
  const path = String(pathname || '');
  if (path === '/perfil' || path === '/agenda' || path.startsWith('/demo/')) return null;
  for (const rule of PATH_PERMISSION_RULES) {
    if (path === rule.prefix || path.startsWith(`${rule.prefix}/`)) {
      return rule.permission;
    }
  }
  return null;
}

/** Paths that accept any of several permissions (e.g. RRHH usable desde Finanzas). */
const HREF_ANY_PERMISSIONS = {
  '/backoffice/hr/collaborators': ['backoffice.dashboard', 'finance.read', 'admin.users'],
  '/backoffice/hr/team': ['backoffice.dashboard', 'finance.read', 'admin.users'],
};

export function canAccessPath(user, pathname) {
  if (isAdministrationAreaPath(pathname)) {
    return canAccessAdministration(user);
  }
  const path = String(pathname || '').split('?')[0];
  const section = findSectionByPath(path);
  if (section) {
    return hasSectionAction(user, section, 'read');
  }
  const anyNeeded = HREF_ANY_PERMISSIONS[path];
  if (Array.isArray(anyNeeded) && anyNeeded.length) {
    return anyNeeded.some((code) => hasPermission(user, code));
  }
  const needed = HREF_PERMISSION[path] || requiredPermissionForPath(path);
  if (!needed) return true;
  return hasPermission(user, needed);
}

export function canAccessHref(user, href) {
  return canAccessPath(user, href);
}

export function filterBusinessUnitsByPermission(businessUnits, user) {
  const next = {};
  Object.entries(businessUnits || {}).forEach(([unitName, unit]) => {
    const sections = (unit.sections || [])
      .map((section) => ({
        ...section,
        items: (section.items || []).filter((item) => canAccessHref(user, item.href)),
      }))
      .filter((section) => section.items.length > 0);
    if (sections.length) {
      next[unitName] = { ...unit, sections };
    }
  });
  return next;
}

export function filterAdministrationCatalog(catalog, user) {
  if (!canAccessAdministration(user)) return [];
  return (catalog || [])
    .map((category) => ({
      ...category,
      items: (category.items || []).filter((item) => {
        const section = findSectionByPath(item.href);
        if (section) return hasSectionAction(user, section, 'read');
        const needed = CATALOG_ITEM_PERMISSION[item.key] || 'admin.access';
        return hasPermission(user, needed);
      }),
    }))
    .filter((category) => category.items.length > 0);
}

export function firstAllowedPath(user, fallback = '/perfil') {
  // Finance dashboard only as home when the user can actually load finance KPIs.
  if (hasPermission(user, 'finance.read') && canAccessPath(user, '/backoffice/dashboard')) {
    return '/backoffice/dashboard';
  }
  const candidates = [
    '/pinturas/commercial/summary',
    '/pinturas/products/paint',
    '/aditivos/commercial/summary',
    '/backoffice/dashboard',
    '/backoffice/finance/overview',
    '/perfil',
    '/agenda',
  ];
  for (const path of candidates) {
    if (canAccessPath(user, path)) return path;
  }
  return fallback;
}
